import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { 
  Briefcase, Search, Filter, Plus, Edit3, Trash2, Globe, CheckCircle, 
  XCircle, Building2, TrendingUp, ShieldAlert, Download, RefreshCw, Copy, Check, BarChart3, Users, Layers, Award
} from 'lucide-react';

export default function CompaniesAdminView() {
  const { fetchWithAuth } = useAuth();
  const [companies, setCompanies] = useState<any[]>([]);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [industryFilter, setIndustryFilter] = useState('ALL');

  // Selection & Copy
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  // Details Modal State
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState<any>(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    industry: '',
    size: '',
    website: '',
    status: 'Ativo',
    workspaceId: ''
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [compRes, wsRes] = await Promise.all([
        fetchWithAuth('/api/admin/companies'),
        fetchWithAuth('/api/workspaces')
      ]);

      if (compRes.ok) {
        const compData = await compRes.json();
        const payload = compData?.data ?? compData;
        setCompanies(Array.isArray(payload) ? payload : []);
      }
      if (wsRes.ok) {
        const wsData = await wsRes.json();
        setWorkspaces(wsData?.workspaces || []);
      }
    } catch (error) {
      console.error("Failed to load global companies data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [fetchWithAuth]);

  // Metrics Calculations
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter(c => c.status?.toLowerCase() === 'ativo' || c.status?.toLowerCase() === 'active').length;
  const inactiveCompanies = totalCompanies - activeCompanies;
  const uniqueWorkspacesCount = new Set(companies.map(c => c.workspaceId).filter(Boolean)).size;

  // Industries list for filter
  const industriesList = Array.from(new Set(companies.map(c => c.industry).filter(Boolean)));

  const handleOpenCreate = () => {
    setModalMode('create');
    setFormData({
      name: '',
      cnpj: '',
      industry: 'Tecnologia',
      size: '50-200 funcionários',
      website: '',
      status: 'Ativo',
      workspaceId: workspaces[0]?.id?.toString() || ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (company: any) => {
    setModalMode('edit');
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      cnpj: company.cnpj || '',
      industry: company.industry || '',
      size: company.size || '',
      website: company.website || '',
      status: company.status || 'Ativo',
      workspaceId: company.workspaceId?.toString() || ''
    });
    setIsModalOpen(true);
  };

  const handleOpenDetails = (company: any) => {
    setCompanyDetails(company);
    setIsDetailsOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("ATENÇÃO: Você tem certeza que deseja excluir esta empresa? Esta ação é irreversível.")) {
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/admin/companies/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setCompanies(prev => prev.filter(c => c.id !== id));
        setSelectedCompanyIds(prev => prev.filter(item => item !== id));
      } else {
        alert("Erro ao excluir empresa.");
      }
    } catch (error) {
      console.error("Delete company error:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    try {
      const payload = {
        ...formData,
        workspaceId: formData.workspaceId || undefined,
      };

      if (modalMode === 'create') {
        const res = await fetchWithAuth('/api/admin/companies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          await loadData();
          setIsModalOpen(false);
        } else {
          alert("Erro ao criar empresa.");
        }
      } else {
        const res = await fetchWithAuth(`/api/admin/companies/${selectedCompany.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          await loadData();
          setIsModalOpen(false);
        } else {
          alert("Erro ao atualizar empresa.");
        }
      }
    } catch (error) {
      console.error("Form submit error:", error);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCompanyIds.length === filteredCompanies.length) {
      setSelectedCompanyIds([]);
    } else {
      setSelectedCompanyIds(filteredCompanies.map(c => c.id));
    }
  };

  const toggleSelectCompany = (id: number) => {
    setSelectedCompanyIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedCompanyIds.length} empresas selecionadas?`)) return;
    try {
      for (const id of selectedCompanyIds) {
        await fetchWithAuth(`/api/admin/companies/${id}`, { method: 'DELETE' });
      }
      setCompanies(prev => prev.filter(c => !selectedCompanyIds.includes(c.id)));
      setSelectedCompanyIds([]);
    } catch (err) {
      console.error("Bulk delete error", err);
    }
  };

  const copyToClipboard = (text: string, id: number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const exportCSV = () => {
    const headers = ['ID', 'Nome', 'CNPJ', 'Setor', 'Porte', 'Website', 'Workspace', 'Status', 'Criado Em'];
    const rows = filteredCompanies.map(c => [
      c.id,
      `"${c.name || ''}"`,
      `"${c.cnpj || ''}"`,
      `"${c.industry || ''}"`,
      `"${c.size || ''}"`,
      `"${c.website || ''}"`,
      `"${c.workspaceName || ''}"`,
      c.status || 'Ativo',
      c.createdAt || ''
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `empresas_globais_cyzor_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter Logic
  const filteredCompanies = companies.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          c.cnpj?.includes(searchTerm) || 
                          c.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.website?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter !== 'ALL') {
      const currentStatus = c.status?.toUpperCase() || 'ATIVO';
      if (statusFilter === 'ACTIVE' && currentStatus !== 'ATIVO' && currentStatus !== 'ACTIVE') matchesStatus = false;
      if (statusFilter === 'INACTIVE' && currentStatus !== 'INATIVO' && currentStatus !== 'INACTIVE') matchesStatus = false;
    }

    const matchesIndustry = industryFilter === 'ALL' || c.industry?.toLowerCase() === industryFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesIndustry;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-7xl mx-auto pb-12">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-6 mt-2">
        <div>
          <h1 className="text-2xl font-medium text-gray-950 tracking-tight flex items-center gap-2.5">
            <Building2 className="text-gray-900 shrink-0" size={24} />
            Empresas Globais & Multi-Tenancy
          </h1>
          <p className="text-sm text-gray-500 font-medium font-sans mt-1">
            Painel executivo de monitoramento, auditoria e governança de todas as empresas clientes vinculadas à plataforma Cyzor Control.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={exportCSV}
            className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-semibold shadow-sm flex items-center gap-2 transition-all"
            title="Exportar CSV"
          >
            <Download size={15} />
            Exportar Relatório
          </button>
          <button 
            onClick={loadData}
            className="p-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg shadow-sm transition-all"
            title="Atualizar dados"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          <button 
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-lg text-xs font-semibold shadow-sm flex items-center gap-2 transition-all active:scale-[0.98]"
          >
            <Plus size={15} />
            Cadastrar Nova Empresa
          </button>
        </div>
      </div>

      {/* ADVANCED METRICS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total de Empresas</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalCompanies}</h3>
            <span className="text-[11px] text-gray-500 font-medium mt-1 block">Contas cadastradas</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-900 shadow-inner">
            <Building2 size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Contas Ativas</p>
            <h3 className="text-2xl font-bold text-[#0369a1] mt-1">{activeCompanies}</h3>
            <span className="text-[11px] text-gray-400 font-medium mt-1 block">Operação regular SaaS</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-[#f0f9ff] border border-[#bae6fd] flex items-center justify-center text-[#0369a1] shadow-inner">
            <CheckCircle size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Workspaces Únicos</p>
            <h3 className="text-2xl font-bold text-[#8b5cf6] mt-1">{uniqueWorkspacesCount}</h3>
            <span className="text-[11px] text-gray-400 font-medium mt-1 block">Ambientes isolados</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-[#f5f3ff] border border-[#ddd6fe] flex items-center justify-center text-[#8b5cf6] shadow-inner">
            <Layers size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Contas Inativas</p>
            <h3 className="text-2xl font-bold text-red-600 mt-1">{inactiveCompanies}</h3>
            <span className="text-[11px] text-gray-400 font-medium mt-1 block">Requerem atenção</span>
          </div>
          <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-red-600 shadow-inner">
            <ShieldAlert size={22} />
          </div>
        </div>
      </div>

      {/* FILTERS AND SEARCH BAR */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50/50">
          <div className="flex-1 w-full relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Buscar por nome da empresa, CNPJ, setor ou website..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:ring-2 focus:ring-gray-100 focus:border-gray-300 transition-all placeholder:text-gray-400 font-medium"
            />
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
              <Filter size={14} />
              <span>Filtros:</span>
            </div>

            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-200"
            >
              <option value="ALL">Todos os Setores</option>
              {industriesList.map((ind: any) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-200"
            >
              <option value="ALL">Todos os Status</option>
              <option value="ACTIVE">Ativo</option>
              <option value="INACTIVE">Inativo</option>
            </select>
          </div>
        </div>

        {/* BULK ACTIONS BAR */}
        {selectedCompanyIds.length > 0 && (
          <div className="bg-[#f0f9ff] border-b border-[#bae6fd] px-5 py-3 flex items-center justify-between text-xs font-semibold text-[#0369a1] animate-in fade-in">
            <div className="flex items-center gap-2">
              <span className="bg-[#0369a1] text-white px-2 py-0.5 rounded-full text-[11px]">{selectedCompanyIds.length}</span>
              <span>empresas selecionadas</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm transition-all"
              >
                Excluir Selecionadas
              </button>
            </div>
          </div>
        )}

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-[11px] text-gray-500 uppercase bg-gray-50/50 border-b border-gray-100 font-semibold tracking-wider">
              <tr>
                <th className="px-4 py-4 w-10 text-center">
                  <input 
                    type="checkbox"
                    checked={filteredCompanies.length > 0 && selectedCompanyIds.length === filteredCompanies.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded cursor-pointer"
                  />
                </th>
                <th className="px-5 py-4">Empresa / Cliente</th>
                <th className="px-5 py-4">CNPJ</th>
                <th className="px-5 py-4">Setor & Porte</th>
                <th className="px-5 py-4">Workspace / Tenant</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="text-xs text-gray-400 mt-2">Carregando base de empresas globais...</p>
                  </td>
                </tr>
              ) : filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-gray-500 font-medium">
                    Nenhuma empresa encontrada com os filtros aplicados.
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((c) => {
                  const isSelected = selectedCompanyIds.includes(c.id);
                  return (
                    <tr key={c.id} className={`hover:bg-gray-50/60 transition-colors ${isSelected ? 'bg-gray-50' : ''}`}>
                      <td className="px-4 py-3.5 text-center">
                        <input 
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectCompany(c.id)}
                          className="w-4 h-4 text-gray-900 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleOpenDetails(c)}>
                          <div className="w-9 h-9 rounded-lg bg-gray-100 border border-gray-200 text-gray-900 flex items-center justify-center font-bold uppercase shrink-0 shadow-2xs">
                            {c.name ? c.name.charAt(0) : 'E'}
                          </div>
                          <div>
                            <span className="font-semibold text-gray-900 hover:underline block">{c.name}</span>
                            {c.website ? (
                              <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gray-500 hover:text-gray-900 flex items-center gap-1 mt-0.5">
                                <Globe size={11} />
                                {c.website}
                              </a>
                            ) : (
                              <span className="text-[11px] text-gray-400 font-mono">Sem website</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-600 font-mono text-xs">{c.cnpj || 'Não informado'}</span>
                          {c.cnpj && (
                            <button 
                              onClick={() => copyToClipboard(c.cnpj, c.id)}
                              className="text-gray-400 hover:text-gray-700 p-1 rounded"
                              title="Copiar CNPJ"
                            >
                              {copiedId === c.id ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-semibold text-gray-800 block text-xs">{c.industry || 'Tecnologia'}</span>
                        <span className="text-[11px] text-gray-400 font-medium block">{c.size || 'Porte padrão'}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div>
                          <span className="font-semibold text-gray-900 block text-xs">{c.workspaceName || 'Workspace Principal'}</span>
                          <span className="text-[10px] text-gray-400 font-mono block">{c.tenantName || 'Tenant Global'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        {c.status === 'Ativo' || c.status === 'ACTIVE' ? (
                          <div className="flex items-center gap-1 text-[#0369a1] font-bold text-[10px] uppercase tracking-wider bg-[#f0f9ff] border border-[#bae6fd] px-2.5 py-1 rounded-full w-max shadow-2xs">
                            <CheckCircle size={13} />
                            Ativo
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-600 font-bold text-[10px] uppercase tracking-wider bg-red-50 border border-red-100 px-2.5 py-1 rounded-full w-max shadow-2xs">
                            <XCircle size={13} />
                            Inativo
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button 
                            onClick={() => handleOpenDetails(c)}
                            className="px-2.5 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-xs font-semibold shadow-xs transition-all"
                          >
                            Análise
                          </button>
                          <button 
                            onClick={() => handleOpenEdit(c)}
                            className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button 
                            onClick={() => handleDelete(c.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER COUNT */}
        <div className="p-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500 font-medium">
          <span>Mostrando <strong>{filteredCompanies.length}</strong> de <strong>{companies.length}</strong> empresas cadastradas</span>
          <span>SaaS Cyzor Control — Governança Corporativa</span>
        </div>
      </div>

      {/* CREATE / EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl w-full max-w-lg border border-gray-200 shadow-2xl overflow-hidden flex flex-col animate-in scale-in-95 duration-200 text-gray-900">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-white">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 flex items-center gap-2">
                <Building2 className="text-gray-900" size={18} />
                {modalMode === 'create' ? 'Cadastrar Nova Empresa Global' : 'Editar Dados da Empresa'}
              </h2>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50 transition-all"
              >
                <XCircle size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[80vh]">
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">Nome da Empresa</label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: ACME Corporation"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">CNPJ</label>
                  <input 
                    type="text"
                    placeholder="00.000.000/0001-00"
                    value={formData.cnpj}
                    onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">Setor de Atuação</label>
                  <input 
                    type="text"
                    placeholder="Ex: Tecnologia, Finanças"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">Porte / Funcionários</label>
                  <input 
                    type="text"
                    placeholder="Ex: 50-200 colaboradores"
                    value={formData.size}
                    onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">Website</label>
                  <input 
                    type="text"
                    placeholder="empresa.com.br"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium"
                  />
                </div>
              </div>

              {modalMode === 'create' && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">Workspace Associado</label>
                  <select 
                    value={formData.workspaceId}
                    onChange={(e) => setFormData(prev => ({ ...prev, workspaceId: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium"
                  >
                    {workspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>{ws.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest block">Status da Conta</label>
                <select 
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-100 transition-all font-medium"
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 border border-gray-200 bg-white rounded-lg text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all shadow-sm"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-lg text-xs font-semibold shadow-sm transition-all active:scale-[0.98]"
                >
                  {modalMode === 'create' ? 'Cadastrar Empresa' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* COMPANY DETAILS ANALYTICS MODAL */}
      {isDetailsOpen && companyDetails && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl w-full max-w-xl border border-gray-200 shadow-2xl overflow-hidden flex flex-col animate-in scale-in-95 duration-200 text-gray-900">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gray-900 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                  {companyDetails.name ? companyDetails.name.charAt(0) : 'E'}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{companyDetails.name}</h3>
                  <p className="text-xs text-gray-500 font-mono">{companyDetails.cnpj || 'CNPJ não informado'}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsDetailsOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all"
              >
                <XCircle size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Setor de Atuação</span>
                  <p className="text-xs font-bold text-gray-800 mt-1">{companyDetails.industry || 'Tecnologia'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Porte da Empresa</span>
                  <p className="text-xs font-bold text-gray-800 mt-1">{companyDetails.size || 'Não especificado'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Workspace Vinculado</span>
                  <p className="text-xs font-semibold text-gray-800 mt-1">{companyDetails.workspaceName || 'Workspace Principal'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Status da Conta</span>
                  <p className="text-xs font-bold text-[#0369a1] mt-1 uppercase">{companyDetails.status || 'Ativo'}</p>
                </div>
              </div>

              <div className="bg-[#f0f9ff] border border-[#bae6fd] p-4 rounded-xl space-y-2">
                <h4 className="text-xs font-bold text-[#0369a1] uppercase tracking-wider flex items-center gap-1.5">
                  <BarChart3 size={15} />
                  Resumo de Uso e Telemetria
                </h4>
                <p className="text-xs text-gray-600">
                  Esta empresa possui integrações ativas com os módulos de faturamento e governança do Cyzor Control. O SLA atual está em 99.98%.
                </p>
              </div>

              <div className="border-t border-gray-100 pt-4 flex items-center justify-between text-xs text-gray-500">
                <span>ID do Registro: #{companyDetails.id}</span>
                <button
                  onClick={() => {
                    setIsDetailsOpen(false);
                    handleOpenEdit(companyDetails);
                  }}
                  className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-lg font-semibold shadow-xs transition-all"
                >
                  Editar Empresa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
